import {CoreDI, Device, DeviceType, EventID, FullscreenManager, IBrowser, inject, Rectangle} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {BottomPanelView} from '@gongxigames/framework/slot/modules/bottompanel/BottomPanelView';
import {BottomPanelEvents} from '@gongxigames/framework/slot/modules/bottompanel/BottomPanelEvents';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

const BottomPanelEventsMapFromView = {
    [BottomPanelView.GAME_RULES_CLICKED]: BottomPanelEvents.GAME_RULES_CLICKED,
    [BottomPanelView.HOME_CLICKED]: BottomPanelEvents.HOME_BUTTON_CLICKED,
    [BottomPanelView.CASHIER_CLICKED]: BottomPanelEvents.CASHIER_CLICKED,
    [BottomPanelView.HISTORY_CLICKED]: BottomPanelEvents.HISTORY_BUTTON_CLICKED,
    [BottomPanelView.SUPPORT_CLICKED]: BottomPanelEvents.SUPPORT_BUTTON_CLICKED,
    [BottomPanelView.LOGOUT_CLICKED]: BottomPanelEvents.LOGOUT_BUTTON_CLICKED
};

export class BottomPanelModule extends Module<BottomPanelView, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BottomPanelModule');
    public static platform: DeviceType = DeviceType.DESKTOP;

    @inject(CoreDI.FullscreenManager)
    protected _fullscreenManager!: FullscreenManager;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.Browser)
    public browser!: IBrowser;

    protected setupStates(): void {
        for (const eventFromView in BottomPanelEventsMapFromView) {
            if (BottomPanelEventsMapFromView.hasOwnProperty(eventFromView)) {
                const event = BottomPanelEventsMapFromView[eventFromView];

                this.onView(eventFromView, this.emitEvent.bind(this, event));
            }
        }

        this.onView(BottomPanelView.SOUND_BUTTON_CLICKED, this.onSoundButtonClicked);
        this.onView(BottomPanelView.FULLSCREEN_CLICKED, this.onFullscreenClicked);
        this.onView(BottomPanelView.BALANCE_FIELD_UPDATED, this.onBalanceFieldUpdated);

        this.on('General.UI.ENABLE', this.enableAllListener);
        this.on('General.UI.DISABLE', this.disableAllListener);
        strictUiEmitter.on('UI.sounds.switchedTo', this.onSoundsChange.bind(this));
        this.on('General.Game.INITIALIZED', this.refreshBalance);
        this.on(LogicEvents.BALANCE_UPDATED, this.onLogicBalanceUpdate, -1);
        this.on(LogicEvents.POINTS_BALANCE_UPDATED, this.refreshPointsBalance);
        this.on(LogicEvents.BUY_RESPONSE, this.refreshBalance);
        this.on(BigWinEvents.WIN_METER_FINISHED, this.onBigWinMeterFinished);
        this.on(FlowModuleEvents.NO_WIN, this.onNoWinSpin);
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted);
        this.on('General.WinPresentation.WIN_METER_FINISHED', this.onWinPresentationWinMeterFinished);
        this.on(AutoplayEvents.START_AUTOPLAY, this.onAutoplayStarted);
        this.on(AutoplayEvents.STOP_AUTOPLAY, this.onAutoplayStopped);
        this.on(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped);
        this.on(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED, this.onFreeSpinsWin);
        this.on(BottomPanelEvents.CHANGE_SOUND_LEVEL, this.onSoundLevelChange);

        // for restore case
        this.on(FlowModuleEvents.FREESPINS_WIN, this.onFreeSpinsWin);
        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreeSpinsWin);
        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);
        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onFadeOutStarted);
        this.on(PickBonusEvents.FINISHED, this.onPickBonusFinished);
    }

    protected onPopupStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreeSpinsIntroStarted();
        }
    }

    protected onFadeOutStarted(_id: symbol, _type: symbol): void {
    }

    protected onPopupClosed(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreeSpinsOutroFinished();
        }

        if (type === GenericPopupType.RESPIN_OUTRO && !this.logic.isLastFreeSpin()) {
            this.refreshBalance();
        }
    }

    public enableAll(reason: symbol): void {
        this.view.enableAllButtons(reason);
    }

    public disableAll(reason: symbol): void {
        this.view.disableAllButtons(reason);
    }

    protected enableAllListener(eventID: symbol | string, reason: symbol): void {
        this.enableAll(reason);
    }

    protected disableAllListener(eventID: symbol | string, reason: symbol): void {
        this.disableAll(reason);
    }

    protected setupViews(): void {
        this.setView(BottomPanelView.NAME);
    }

    protected onSoundButtonClicked(id: string | symbol, isOn: boolean): void {
        strictUiEmitter.emit(isOn ? 'UI.sounds.enable' : 'UI.sounds.disable');
    }

    protected emitEvent(event: EventID): void {
        this.eventManager.emit(event);
    }

    protected onFullscreenClicked(eventName: any, isFullscreen: boolean): void {
        this.eventManager.emit(BottomPanelEvents.FULLSCREEN_CLICKED, isFullscreen);

        if (this._device.fullscreen) {
            if (this._fullscreenManager.isFullscreen()) {
                this._fullscreenManager.exitFullscreen();
            } else {
                this._fullscreenManager.requestFullscreen();
            }
        }
    }

    protected onWinPresentationWinMeterFinished(): void {
        this.validateToRefreshBalance();
    }

    protected onLogicBalanceUpdate(): void {
        this.refreshBalance();
    }

    protected onNoWinSpin(): void {
        this.validateToRefreshBalance();
    }

    protected onPickBonusFinished(): void {
        this.validateToRefreshBalance();
    }

    protected onBigWinMeterFinished(): void {
        this.validateToRefreshBalance();
    }

    protected validateToRefreshBalance(): void {
        const isRespin = this.logic.previousAction === Action.RESPIN;

        if (!this.logic.isLastFreeSpin() && !isRespin) {
            this.refreshBalance();
        }
    }

    protected refreshBalance(): void {
        this.eventManager.emit('General.UI.BALANCE.UPDATED', this.logic.getBalance());
        this.view.refreshBalance(this.logic.getBalance());
    }

    protected refreshPointsBalance(_id: symbol, balance: number): void {
        this.view.refreshPointsBalance(balance);
    }

    protected onAutoplayStarted(): void {
        this.disableAll(UIDisableReason.AUTOPLAY_MODE);
    }

    protected onAutoplayStopped(): void {
        this.enableAll(UIDisableReason.AUTOPLAY_MODE);
    }

    protected onReelsSpinningStarted(): void {
        this.browser.addTimeout(() => this.refreshBalance());
        this.disableAll(UIDisableReason.SPINNING);
    }

    protected onAllReelsStopped(): void {
        this.enableAll(UIDisableReason.SPINNING);
    }

    protected onFreeSpinsWin(): void {
        this.disableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsIntroStarted(): void {
        this.enableAll(UIDisableReason.TRIGGERING_FEATURE);
        this.disableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsOutroFinished(): void {
        this.refreshBalance();

        this.enableAll(UIDisableReason.TRIGGERING_FEATURE);
        this.enableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onSoundsChange(enabled: boolean): void {
        this.view.setSoundCheckboxValue(enabled);
    }

    protected onSoundLevelChange(eventId: symbol, level: number): void {
        this.view.setSoundSliderValue(level);
    }

    protected onBalanceFieldUpdated(eventId: string, bounds: Rectangle): void {
        this.eventManager.emit('General.UI.BALANCE_FIELD.UPDATED', bounds);
    }
}
